<template>
    <div class="contrast-viewer" :class="splitDirection" ref="contrastViewer">
        <!-- 顶部工具栏 -->
        <div class="contrast-toolbar">
            <label class="sync-label">
                <input type="checkbox" v-model="synced" /> 同步
            </label>
            <button class="tool-btn split-toggle" @click="toggleSplitDirection" :title="splitDirection === 'horizontal' ? '切换为上下分屏' : '切换为左右分屏'">{{ splitDirection === 'horizontal' ? '⇔' : '⇕' }}</button>
            <template v-if="!viewMode">
                <button class="tool-btn" :class="{ active: activeTool === 'hand' }" @click="selectTool('hand')">✋</button>
                <button class="tool-btn" :class="{ active: activeTool === 'circle' }" @click="selectTool('circle')">⭕</button>
                <button class="tool-btn" :class="{ active: activeTool === 'rectangle' }" @click="selectTool('rectangle')">▢</button>
                <button class="tool-btn" :class="{ active: activeTool === 'arrow' }" @click="selectTool('arrow')">➚</button>
                <button class="tool-btn" :class="{ active: activeTool === 'pen' }" @click="selectTool('pen')">✏️</button>
                <button class="tool-btn" :class="{ active: activeTool === 'comment' }" @click="selectTool('comment')">💬</button>
                <button class="tool-btn" @click="undo" :disabled="topState.shapes.length === 0 && bottomState.shapes.length === 0">↩️</button>
                <span class="separator"></span>
                <label class="color-label" title="线条颜色">
                    <input type="color" class="color-picker" :value="currentColor" @input="onColorChange" />
                </label>
                <button class="tool-btn delete-btn" @click="deleteSelected" :disabled="(topState.selectedShapeIndex < 0 && topState.selectedAnnoId === '') && (bottomState.selectedShapeIndex < 0 && bottomState.selectedAnnoId === '')" title="删除选中元素">🗑️</button>
            </template>
            <template v-else>
                <span class="view-mode-tip">只读模式</span>
            </template>
        </div>

        <!-- 上方画布 -->
        <div class="canvas-section top-section" :style="topSectionStyle">
            <div class="version-badge top">{{ topVersion }}(新版)</div>
            <div
                class="canvas-container"
                ref="topContainer"
                @mousedown="handleMouseDown($event, 'top')"
                @mousemove="handleMouseMove($event, 'top')"
                @mouseup="handleMouseUp($event, 'top')"
                @mouseleave="handleMouseUp($event, 'top')"
                @wheel.prevent="handleMouseWheel($event, 'top')"
            >
                <div class="transform-layer" :style="getTransformStyle('top')">
                    <canvas v-show="topState.pdfDoc" ref="topPdfCanvas" class="base-image"></canvas>
                    <canvas ref="topDrawCanvas" class="draw-canvas"></canvas>
                    <div
                        v-for="anno in topState.annotations"
                        :key="anno.id"
                        class="annotation-marker"
                        :class="{ 'marker-selected': topState.selectedAnnoId === anno.id, 'marker-new': isNewAnno(anno), 'marker-end': anno.status === 'End' }"
                        :style="getAnnotationStyle(anno, topState)"
                    >
                        <div class="marker-icon" :style="{ transform: 'scale(' + getScale('top') + ')', transformOrigin: '0 0' }" @mousedown.stop="startMarkerDrag(anno, $event, 'top')"></div>
                        <div
                            class="popup-card"
                            :class="{ active: topState.activePopup === anno.id }"
                            :style="{ left: anno.popupOffsetX || '30px', top: anno.popupOffsetY || '-10px' }"
                            @mousedown.stop
                            @click.stop
                        >
                            <div class="popup-header">
                                <input class="popup-title-input" v-model="anno.title" :readonly="anno._historical || viewMode" placeholder="输入标题" />
                                <button class="close-btn" @click.stop="togglePopup(anno.id, 'top')">×</button>
                            </div>
                            <div class="popup-content">
                                <textarea class="popup-content-textarea" v-model="anno.content" :readonly="anno._historical || viewMode" placeholder="输入批注内容"></textarea>
                            </div>
                            <div v-if="viewMode && replyingAnnoId === anno.id" class="reply-input-area">
                                <textarea class="reply-textarea" v-model="replyCache" placeholder="输入回复内容"></textarea>
                                <div class="reply-actions">
                                    <button class="save-reply-btn" @click.stop="saveReply(anno)">保存回复</button>
                                    <button class="cancel-reply-btn" @click.stop="cancelReply">取消</button>
                                </div>
                            </div>
                            <div v-if="anno.replyContent && replyingAnnoId !== anno.id" class="popup-reply">
                                <span class="reply-label">回复：</span>
                                <span class="reply-text">{{ anno.replyContent }}</span>
                            </div>
                            <div class="popup-footer">
                                <span class="author">{{ anno.replyName || anno.author }}</span>
                                <span v-if="viewMode && anno.replyContent && anno.status !== 'End' && replyingAnnoId !== anno.id" class="edit-reply-btn" @click.stop="editReply(anno)">编辑</span>
                                <span v-if="viewMode && !anno.replyContent && anno.status !== 'End' && replyingAnnoId !== anno.id" class="reply-btn" @click.stop="startReply(anno)">回复</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="canvas-controls">
                <div class="zoom-controls">
                    <button class="zoom-btn" @click="zoomIn('top')">+</button>
                    <div class="zoom-level">{{ topState.zoom }}%</div>
                    <button class="zoom-btn" @click="zoomOut('top')">−</button>
                    <div class="zoom-separator"></div>
                    <button class="zoom-btn" @click="rotateCycle('top')" title="旋转"><span class="rotate-icon" :style="{ transform: 'rotate(' + topState.rotation + 'deg)' }">↻</span></button>
                    <button class="zoom-btn" @click="resetRotation('top')" :disabled="topState.rotation === 0" title="重置旋转">⟲</button>
                </div>
                <div class="pagination">
                    <button class="page-btn" @click="prevPage('top')" :disabled="topState.currentPage <= 1">&lt;</button>
                    <span class="page-info">{{ topState.currentPage }} / {{ topState.totalPages }}</span>
                    <button class="page-btn" @click="nextPage('top')" :disabled="topState.currentPage >= topState.totalPages">&gt;</button>
                </div>
            </div>
        </div>

        <!-- 可拖拽分割线 -->
        <div class="canvas-divider" :class="splitDirection"
             @mousedown="startDividerDrag"
             :style="dividerStyle">
            <div class="divider-handle">⋮</div>
        </div>

        <!-- 下方画布 -->
        <div class="canvas-section bottom-section" :style="bottomSectionStyle">
            <div class="version-badge bottom">{{ bottomVersion }}(旧版)</div>
            <div class="readonly-badge">只读</div>
            <div
                class="canvas-container"
                ref="bottomContainer"
                @mousedown="handleMouseDown($event, 'bottom')"
                @mousemove="handleMouseMove($event, 'bottom')"
                @mouseup="handleMouseUp($event, 'bottom')"
                @mouseleave="handleMouseUp($event, 'bottom')"
                @wheel.prevent="handleMouseWheel($event, 'bottom')"
            >
                <div class="transform-layer" :style="getTransformStyle('bottom')">
                    <canvas v-show="bottomState.pdfDoc" ref="bottomPdfCanvas" class="base-image"></canvas>
                    <canvas ref="bottomDrawCanvas" class="draw-canvas"></canvas>
                    <div
                        v-for="anno in bottomState.annotations"
                        :key="anno.id"
                        class="annotation-marker"
                        :class="{ 'marker-selected': bottomState.selectedAnnoId === anno.id, 'marker-new': isNewAnno(anno), 'marker-end': anno.status === 'End' }"
                        :style="getAnnotationStyle(anno, bottomState)"
                    >
                        <div class="marker-icon" :style="{ transform: 'scale(' + getScale('bottom') + ')', transformOrigin: '0 0' }" @mousedown.stop="startMarkerDrag(anno, $event, 'bottom')"></div>
                        <div
                            class="popup-card"
                            :class="{ active: bottomState.activePopup === anno.id }"
                            :style="{ left: anno.popupOffsetX || '30px', top: anno.popupOffsetY || '-10px' }"
                            @mousedown.stop
                            @click.stop
                        >
                            <div class="popup-header">
                                <input class="popup-title-input" v-model="anno.title" readonly placeholder="输入标题" />
                                <button class="close-btn" @click.stop="togglePopup(anno.id, 'bottom')">×</button>
                            </div>
                            <div class="popup-content">
                                <textarea class="popup-content-textarea" v-model="anno.content" readonly placeholder="输入批注内容"></textarea>
                            </div>
                            <div v-if="anno.replyContent" class="popup-reply">
                                <span class="reply-label">回复：</span>
                                <span class="reply-text">{{ anno.replyContent }}</span>
                            </div>
                            <div class="popup-footer">
                                <span class="author">{{ anno.replyName || anno.author }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="canvas-controls">
                <div class="zoom-controls">
                    <button class="zoom-btn" @click="zoomIn('bottom')">+</button>
                    <div class="zoom-level">{{ bottomState.zoom }}%</div>
                    <button class="zoom-btn" @click="zoomOut('bottom')">−</button>
                    <div class="zoom-separator"></div>
                    <button class="zoom-btn" @click="rotateCycle('bottom')" title="旋转"><span class="rotate-icon" :style="{ transform: 'rotate(' + bottomState.rotation + 'deg)' }">↻</span></button>
                    <button class="zoom-btn" @click="resetRotation('bottom')" :disabled="bottomState.rotation === 0" title="重置旋转">⟲</button>
                </div>
                <div class="pagination">
                    <button class="page-btn" @click="prevPage('bottom')" :disabled="bottomState.currentPage <= 1">&lt;</button>
                    <span class="page-info">{{ bottomState.currentPage }} / {{ bottomState.totalPages }}</span>
                    <button class="page-btn" @click="nextPage('bottom')" :disabled="bottomState.currentPage >= bottomState.totalPages">&gt;</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ContrastCenterPanel",
    props: {
        topBaseImageUrl: { type: String, default: "" },
        bottomBaseImageUrl: { type: String, default: "" },
        topCurrentPage: { type: Number, default: 1 },
        bottomCurrentPage: { type: Number, default: 1 },
        topTotalPages: { type: Number, default: 0 },
        bottomTotalPages: { type: Number, default: 0 },
        topZoom: { type: Number, default: 100 },
        bottomZoom: { type: Number, default: 100 },
        topPageId: { type: String, default: "" },
        bottomPageId: { type: String, default: "" },
        topVersion: { type: String, default: "" },
        bottomVersion: { type: String, default: "" },
        loginName: { type: String, default: "" },
        userName: { type: String, default: "" },
        userId: { type: String, default: "" },
        viewMode: { type: Boolean, default: false },
    },
    data() {
        return {
            synced: false,
            splitDirection: 'horizontal',
            dividerPercent: 50,
            activeTool: 'hand',
            currentColor: '#ff0000',
            replyingAnnoId: '',
            replyCache: '',
            topState: {
                pdfDoc: null,
                translateX: 0, translateY: 0,
                rotation: 0,
                zoom: 100,
                currentPage: 1, totalPages: 0,
                pdfViewport: null,
                shapes: [],
                annotations: [],
                activePopup: null,
                isDragging: false, isDrawing: false,
                dragStartX: 0, dragStartY: 0,
                drawStartX: 0, drawStartY: 0,
                currentPoints: [],
                selectedShapeIndex: -1,
                selectedAnnoId: '',
                pageId: '',
            },
            bottomState: {
                pdfDoc: null,
                translateX: 0, translateY: 0,
                rotation: 0,
                zoom: 100,
                currentPage: 1, totalPages: 0,
                pdfViewport: null,
                shapes: [],
                annotations: [],
                activePopup: null,
                isDragging: false, isDrawing: false,
                dragStartX: 0, dragStartY: 0,
                drawStartX: 0, drawStartY: 0,
                currentPoints: [],
                selectedShapeIndex: -1,
                selectedAnnoId: '',
                pageId: '',
            },
        };
    },
    watch: {
        topBaseImageUrl(url) {
            if (url) this.loadPdf('top', url);
        },
        bottomBaseImageUrl(url) {
            if (url) this.loadPdf('bottom', url);
        },
        topCurrentPage() {
            this.syncPageFromProp('top');
        },
        bottomCurrentPage() {
            this.syncPageFromProp('bottom');
        },
        'topState.annotations': {
            deep: true,
            handler() {
                this.emitCacheSync('top');
            },
        },
        'topState.shapes': {
            deep: true,
            handler() {
                this.emitCacheSync('top');
            },
        },
        'bottomState.annotations': {
            deep: true,
            handler() {
                this.emitCacheSync('bottom');
            },
        },
        'bottomState.shapes': {
            deep: true,
            handler() {
                this.emitCacheSync('bottom');
            },
        },
    },
    computed: {
        topSectionStyle() {
            if (this.splitDirection === 'horizontal') {
                return { width: this.dividerPercent + '%' };
            }
            return { height: this.dividerPercent + '%' };
        },
        dividerStyle() {
            if (this.splitDirection === 'horizontal') {
                return { left: this.dividerPercent + '%' };
            }
            return { top: this.dividerPercent + '%' };
        },
        bottomSectionStyle() {
            if (this.splitDirection === 'horizontal') {
                return { left: 'calc(' + this.dividerPercent + '% + 4px)' };
            }
            return { top: 'calc(' + this.dividerPercent + '% + 4px)' };
        },
    },
    mounted() {
        if (this.topBaseImageUrl) this.loadPdf('top', this.topBaseImageUrl);
        if (this.bottomBaseImageUrl) this.loadPdf('bottom', this.bottomBaseImageUrl);
    },
    methods: {
        getState(key) {
            return key === 'top' ? this.topState : this.bottomState;
        },

        getScale(key) {
            return this.getState(key).zoom / 100;
        },

        getTransformStyle(key) {
            const s = this.getState(key);
            return {
                transform: `translate(${s.translateX}px, ${s.translateY}px)`,
                transformOrigin: '0 0',
            };
        },

        toCanvasCoords(x, y, state) {
            const s = state.zoom / 100;
            const ow = state.pdfViewport ? state.pdfViewport.width : 1;
            const oh = state.pdfViewport ? state.pdfViewport.height : 1;
            switch (state.rotation) {
                case 90:  return { x: (oh - y) * s, y: x * s };
                case 180: return { x: (ow - x) * s, y: (oh - y) * s };
                case 270: return { x: y * s, y: (ow - x) * s };
                default:  return { x: x * s, y: y * s };
            }
        },

        toLogicalCoords(cx, cy, state) {
            const s = state.zoom / 100;
            const ow = state.pdfViewport ? state.pdfViewport.width : 1;
            const oh = state.pdfViewport ? state.pdfViewport.height : 1;
            switch (state.rotation) {
                case 90:  return { x: cy / s, y: oh - cx / s };
                case 180: return { x: ow - cx / s, y: oh - cy / s };
                case 270: return { x: ow - cy / s, y: cx / s };
                default:  return { x: cx / s, y: cy / s };
            }
        },

        getAnnotationStyle(anno, state) {
            if (!state.pdfViewport) return { left: '0%', top: '0%' };
            const logicalX = (anno.x / 100) * state.pdfViewport.width;
            const logicalY = (anno.y / 100) * state.pdfViewport.height;
            const pos = this.toCanvasCoords(logicalX, logicalY, state);
            return { left: pos.x + 'px', top: pos.y + 'px' };
        },

        isNewAnno(anno) {
            if (!anno || anno._historical) return false;
            return typeof anno === 'string' ? anno.startsWith('anno_') : (anno.id && anno.id.startsWith('anno_'));
        },

        selectTool(tool) {
            this.activeTool = tool;
            this.topState.selectedShapeIndex = -1;
            this.topState.selectedAnnoId = '';
            this.bottomState.selectedShapeIndex = -1;
            this.bottomState.selectedAnnoId = '';
        },

        onColorChange(e) {
            const color = e.target.value;
            this.currentColor = color;
            ['top', 'bottom'].forEach(key => {
                const s = this.getState(key);
                if (s.selectedShapeIndex >= 0 && s.shapes[s.selectedShapeIndex]) {
                    s.shapes[s.selectedShapeIndex].color = color;
                    this.redrawAllShapes(key);
                }
            });
        },

        async deleteSelected() {
            const s = this.topState;
            if (s.selectedShapeIndex >= 0) {
                const shape = s.shapes[s.selectedShapeIndex];
                if (shape && shape.id && !String(shape.id).startsWith('shape_') && !this.isNewAnno({ id: shape.id })) {
                    try {
                        await deleteAuditMarkupComment(shape.id);
                    } catch (e) {
                        console.error('删除批注失败', e);
                    }
                }
                s.shapes.splice(s.selectedShapeIndex, 1);
                s.selectedShapeIndex = -1;
                this.redrawAllShapes('top');
            } else if (s.selectedAnnoId) {
                const anno = s.annotations.find(a => a.id === s.selectedAnnoId);
                if (anno && anno.id && !this.isNewAnno(anno)) {
                    try {
                        await deleteAuditMarkupComment(anno.id);
                    } catch (e) {
                        console.error('删除批注失败', e);
                    }
                }
                s.annotations = s.annotations.filter(a => a.id !== s.selectedAnnoId);
                s.selectedAnnoId = '';
                s.activePopup = null;
            }
        },

        undo() {
            const s = this.topState;
            if (s.shapes.length > 0) {
                s.shapes.pop();
                this.redrawAllShapes('top');
            }
        },

        getActiveState() {
            return this.topState;
        },

        loadPdf(key, url) {
            const s = this.getState(key);
            if (s._pdfLoadingTask) {
                s._pdfLoadingTask.destroy();
                s._pdfLoadingTask = null;
            }
            s.pdfDoc = null;
            if (s._renderTask) {
                s._renderTask.cancel();
                s._renderTask = null;
            }
            s.translateX = 0;
            s.translateY = 0;
            s.rotation = 0;
            s._pdfLoadingTask = window.pdfjsLib.getDocument(url);
            s._pdfLoadingTask.promise.then((pdf) => {
                s.pdfDoc = pdf;
                this.$emit("update:" + key + "TotalPages", pdf.numPages);
                if (s.currentPage > pdf.numPages) {
                    this.setPage(key, 1);
                } else {
                    this.renderPage(key);
                }
            }).catch((err) => {
                if (err?.name !== 'RenderingCancelledException') {
                    console.error("pdfjs load error", err);
                }
            });
        },

        renderPage(key) {
            const s = this.getState(key);
            if (!s.pdfDoc) return;

            if (s._renderTask) {
                s._renderTask.cancel();
                s._renderTask = null;
            }

            const refs = {
                top: { pdf: this.$refs.topPdfCanvas, draw: this.$refs.topDrawCanvas, container: this.$refs.topContainer },
                bottom: { pdf: this.$refs.bottomPdfCanvas, draw: this.$refs.bottomDrawCanvas, container: this.$refs.bottomContainer },
            };
            const pdfCanvas = refs[key].pdf;
            const drawCanvas = refs[key].draw;
            const container = refs[key].container;
            if (!pdfCanvas || !drawCanvas || !container) return;

            const scale = s.zoom / 100;
            s.pdfDoc.getPage(s.currentPage).then((page) => {
                // pdf.js 的 getViewport({ rotation }) 会用传入值“替换”页面自带的 /Rotate（绝对旋转，非叠加）。
                // rotation=0（默认）必须按页面 /Rotate 正常显示；用户的 s.rotation 是在正常方向上的附加旋转。
                const intrinsicRotation = (((page.rotate || 0) % 360) + 360) % 360;
                const rotation = (s.rotation + intrinsicRotation) % 360;
                s.pdfViewport = page.getViewport({ scale: 1, rotation: intrinsicRotation });

                // 按 devicePixelRatio 放大画布实际分辨率，避免高分屏下模糊；CSS 尺寸保持逻辑大小
                // 缩小时按不低于基准分辨率渲染、再由 CSS 缩小显示，避免直接低分辨率渲染导致文字发虚
                const dpr = window.devicePixelRatio || 1;
                let renderScale = Math.max(scale, 1) * dpr;
                // 防止超大缩放下画布尺寸超出浏览器限制导致渲染失败
                const maxScale = Math.min(16384 / s.pdfViewport.width, 16384 / s.pdfViewport.height, Math.sqrt(134217728 / (s.pdfViewport.width * s.pdfViewport.height)));
                if (renderScale > maxScale) renderScale = maxScale;
                const viewport = page.getViewport({ scale: scale, rotation });
                const renderViewport = page.getViewport({ scale: renderScale, rotation });

                pdfCanvas.width = renderViewport.width;
                pdfCanvas.height = renderViewport.height;
                pdfCanvas.style.width = viewport.width + 'px';
                pdfCanvas.style.height = viewport.height + 'px';
                // 绘制层位图按 CSS 尺寸 × dpr 设置：缩小时 PDF 层以不低于基准分辨率渲染再由 CSS 缩小显示，
                // 若绘制层跟随 renderViewport，其位图与 CSS 尺寸不再是 dpr 倍关系，redrawAllShapes 的 dpr 变换
                // 会导致缩小时绘制内容偏离鼠标位置（放大时 renderScale = scale * dpr，二者恰好一致所以正常）
                drawCanvas.width = viewport.width * dpr;
                drawCanvas.height = viewport.height * dpr;
                drawCanvas.style.width = viewport.width + 'px';
                drawCanvas.style.height = viewport.height + 'px';

                const ctx = pdfCanvas.getContext("2d");
                s._renderTask = page.render({ canvasContext: ctx, viewport: renderViewport });
                s._renderTask.promise.then(() => {
                    s._renderTask = null;
                    this.centerCanvas(key);
                    this.redrawAllShapes(key);
                }).catch((err) => {
                    if (err?.name !== 'RenderingCancelledException') {
                        console.error("render error", err);
                    }
                });
            });
        },

        centerCanvas(key) {
            const s = this.getState(key);
            const container = key === 'top' ? this.$refs.topContainer : this.$refs.bottomContainer;
            const pdfCanvas = key === 'top' ? this.$refs.topPdfCanvas : this.$refs.bottomPdfCanvas;
            if (!container || !pdfCanvas) return;
            const dpr = window.devicePixelRatio || 1;
            s.translateX = (container.offsetWidth - pdfCanvas.width / dpr) / 2;
            s.translateY = (container.offsetHeight - pdfCanvas.height / dpr) / 2;
        },

        getPdfCoordinates(e, key) {
            const container = key === 'top' ? this.$refs.topContainer : this.$refs.bottomContainer;
            if (!container) return { x: 0, y: 0 };
            const rect = container.getBoundingClientRect();
            const containerX = e.clientX - rect.left;
            const containerY = e.clientY - rect.top;
            const s = this.getState(key);
            const canvasX = containerX - s.translateX;
            const canvasY = containerY - s.translateY;
            return this.toLogicalCoords(canvasX, canvasY, s);
        },

        handleMouseDown(e, key) {
            const s = this.getState(key);
            if (key === 'bottom' && this.activeTool !== 'hand') return;
            if (this.activeTool === 'hand') {
                const pos = this.getPdfCoordinates(e, key);
                const shapeIdx = this.getShapeAtPoint(pos.x, pos.y, s);
                if (shapeIdx >= 0) {
                    s.selectedShapeIndex = shapeIdx;
                    s.selectedAnnoId = '';
                    this.currentColor = s.shapes[shapeIdx].color || this.currentColor;
                    return;
                }
                s.selectedShapeIndex = -1;
                s.selectedAnnoId = '';
                s.isDragging = true;
                s.dragStartX = e.clientX - s.translateX;
                s.dragStartY = e.clientY - s.translateY;
                return;
            }

            if (this.activeTool === 'comment') {
                const pos = this.getPdfCoordinates(e, key);
                const xPercent = s.pdfViewport ? (pos.x / s.pdfViewport.width) * 100 : 0;
                const yPercent = s.pdfViewport ? (pos.y / s.pdfViewport.height) * 100 : 0;
                const newAnno = {
                    id: 'anno_' + Date.now(),
                    title: '',
                    content: '',
                    author: this.userName || '当前用户',
                    creator: this.userName || '当前用户',
                    createName: this.userName || '当前用户',
                    x: xPercent,
                    y: yPercent,
                    popupOffsetX: '30px',
                    popupOffsetY: '-10px'
                };
                s.annotations.push(newAnno);
                s.activePopup = newAnno.id;
                this.activeTool = 'hand';
                this.$emit('add-annotation', newAnno);
                return;
            }

            if (['circle', 'rectangle', 'arrow'].includes(this.activeTool)) {
                const pos = this.getPdfCoordinates(e, key);
                s.isDrawing = true;
                s.drawStartX = pos.x;
                s.drawStartY = pos.y;
            } else if (this.activeTool === 'pen') {
                const pos = this.getPdfCoordinates(e, key);
                s.isDrawing = true;
                s.currentPoints = [{ x: pos.x, y: pos.y }];
            }
        },

        handleMouseMove(e, key) {
            const s = this.getState(key);
            if (key === 'bottom' && !(this.activeTool === 'hand' && s.isDragging)) return;
            if (this.activeTool === 'hand' && s.isDragging) {
                const newTranslateX = e.clientX - s.dragStartX;
                const newTranslateY = e.clientY - s.dragStartY;
                const dx = newTranslateX - s.translateX;
                const dy = newTranslateY - s.translateY;
                s.translateX = newTranslateX;
                s.translateY = newTranslateY;
                if (this.synced) {
                    const other = key === 'top' ? this.bottomState : this.topState;
                    other.translateX += dx;
                    other.translateY += dy;
                }
                return;
            }
            if (!s.isDrawing) return;
            const pos = this.getPdfCoordinates(e, key);
            const canvas = key === 'top' ? this.$refs.topDrawCanvas : this.$refs.bottomDrawCanvas;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            this.redrawAllShapes(key);
            ctx.strokeStyle = this.currentColor;
            ctx.lineWidth = 2;

            const start = this.toCanvasCoords(s.drawStartX, s.drawStartY, s);
            const cur = this.toCanvasCoords(pos.x, pos.y, s);

            if (this.activeTool === 'circle') {
                const radius = Math.sqrt(Math.pow(cur.x - start.x, 2) + Math.pow(cur.y - start.y, 2));
                ctx.beginPath();
                ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI);
                ctx.stroke();
            } else if (this.activeTool === 'rectangle') {
                ctx.strokeRect(start.x, start.y, cur.x - start.x, cur.y - start.y);
            } else if (this.activeTool === 'arrow') {
                this.drawArrow(ctx, start.x, start.y, cur.x, cur.y);
            } else if (this.activeTool === 'pen') {
                s.currentPoints.push({ x: pos.x, y: pos.y });
                ctx.beginPath();
                const first = this.toCanvasCoords(s.currentPoints[0].x, s.currentPoints[0].y, s);
                ctx.moveTo(first.x, first.y);
                for (let i = 1; i < s.currentPoints.length; i++) {
                    const pt = this.toCanvasCoords(s.currentPoints[i].x, s.currentPoints[i].y, s);
                    ctx.lineTo(pt.x, pt.y);
                }
                ctx.stroke();
            }
        },

        handleMouseUp(e, key) {
            const s = this.getState(key);
            if (this.activeTool === 'hand') {
                s.isDragging = false;
                return;
            }
            if (key === 'bottom') return;
            if (!s.isDrawing) return;
            s.isDrawing = false;
            const color = this.currentColor;
            const pos = this.getPdfCoordinates(e, key);
            if (this.activeTool === 'pen') {
                s.shapes.push({ type: 'pen', color, points: [...s.currentPoints] });
            } else if (this.activeTool === 'circle') {
                const dx = pos.x - s.drawStartX;
                const dy = pos.y - s.drawStartY;
                const radius = Math.sqrt(dx * dx + dy * dy);
                s.shapes.push({ type: 'circle', color, x: s.drawStartX, y: s.drawStartY, radius });
            } else if (this.activeTool === 'rectangle') {
                s.shapes.push({ type: 'rectangle', color, x: s.drawStartX, y: s.drawStartY, w: pos.x - s.drawStartX, h: pos.y - s.drawStartY });
            } else if (this.activeTool === 'arrow') {
                s.shapes.push({ type: 'arrow', color, x1: s.drawStartX, y1: s.drawStartY, x2: pos.x, y2: pos.y });
            }
            s.selectedShapeIndex = s.shapes.length - 1;
            s.currentPoints = [];
            this.redrawAllShapes(key);
        },

        handleMouseWheel(e, key) {
            if (e.deltaY < 0) {
                this.zoomIn(key);
            } else {
                this.zoomOut(key);
            }
        },

        zoomIn(key) {
            const s = this.getState(key);
            if (s.zoom >= 500) return;
            const oldZoom = s.zoom;
            const newZoom = oldZoom + 10;
            this.adjustTranslateForZoom(key, oldZoom, newZoom);
            s.zoom = newZoom;
            this.renderPage(key);
            if (this.synced) {
                const other = key === 'top' ? this.bottomState : this.topState;
                const otherKey = key === 'top' ? 'bottom' : 'top';
                other.zoom = newZoom;
                this.renderPage(otherKey);
            }
        },

        zoomOut(key) {
            const s = this.getState(key);
            if (s.zoom <= 10) return;
            const oldZoom = s.zoom;
            const newZoom = oldZoom - 10;
            this.adjustTranslateForZoom(key, oldZoom, newZoom);
            s.zoom = newZoom;
            this.renderPage(key);
            if (this.synced) {
                const other = key === 'top' ? this.bottomState : this.topState;
                const otherKey = key === 'top' ? 'bottom' : 'top';
                other.zoom = newZoom;
                this.renderPage(otherKey);
            }
        },

        adjustTranslateForZoom(key, oldZoom, newZoom) {
            const s = this.getState(key);
            const container = key === 'top' ? this.$refs.topContainer : this.$refs.bottomContainer;
            if (!container) return;
            const centerX = container.offsetWidth / 2;
            const centerY = container.offsetHeight / 2;
            const oldScale = oldZoom / 100;
            const pointX = (centerX - s.translateX) / oldScale;
            const pointY = (centerY - s.translateY) / oldScale;
            const newScale = newZoom / 100;
            s.translateX = centerX - pointX * newScale;
            s.translateY = centerY - pointY * newScale;
        },

        rotateCycle(key) {
            const s = this.getState(key);
            s.rotation = (s.rotation + 90) % 360;
            this.renderPage(key);
            this.emitCacheSync(key);
            if (this.synced) {
                const other = key === 'top' ? this.bottomState : this.topState;
                const otherKey = key === 'top' ? 'bottom' : 'top';
                other.rotation = s.rotation;
                this.renderPage(otherKey);
                this.emitCacheSync(otherKey);
            }
        },

        resetRotation(key) {
            const s = this.getState(key);
            s.rotation = 0;
            this.renderPage(key);
            this.emitCacheSync(key);
            if (this.synced) {
                const other = key === 'top' ? this.bottomState : this.topState;
                const otherKey = key === 'top' ? 'bottom' : 'top';
                other.rotation = 0;
                this.renderPage(otherKey);
                this.emitCacheSync(otherKey);
            }
        },

        setPage(key, page) {
            const s = this.getState(key);
            if (page < 1 || (s.pdfDoc && page > s.pdfDoc.numPages)) return;
            s.currentPage = page;
            s.translateX = 0;
            s.translateY = 0;
            s.rotation = 0;
            this.renderPage(key);
            this.emitCacheSync(key);
            this.$emit("update:" + key + "CurrentPage", page);
        },

        prevPage(key) {
            const s = this.getState(key);
            if (s.currentPage > 1) this.setPage(key, s.currentPage - 1);
        },
        nextPage(key) {
            const s = this.getState(key);
            if (s.currentPage < s.totalPages) this.setPage(key, s.currentPage + 1);
        },

        syncPageFromProp(key) {
            const s = this.getState(key);
            const propPage = key === 'top' ? this.topCurrentPage : this.bottomCurrentPage;
            if (propPage !== s.currentPage) {
                s.currentPage = propPage;
                s.translateX = 0;
                s.translateY = 0;
                s.rotation = 0;
                this.renderPage(key);
                this.emitCacheSync(key);
            }
        },

        getShapeAtPoint(pdfX, pdfY, s) {
            const threshold = 12 / (s.zoom / 100);
            for (let i = s.shapes.length - 1; i >= 0; i--) {
                const shape = s.shapes[i];
                if (shape.type === 'circle') {
                    const d = Math.sqrt((pdfX - shape.x) ** 2 + (pdfY - shape.y) ** 2);
                    if (Math.abs(d - shape.radius) < threshold) return i;
                } else if (shape.type === 'rectangle') {
                    const x1 = Math.min(shape.x, shape.x + shape.w), x2 = Math.max(shape.x, shape.x + shape.w);
                    const y1 = Math.min(shape.y, shape.y + shape.h), y2 = Math.max(shape.y, shape.y + shape.h);
                    if (pdfX >= x1 - threshold && pdfX <= x2 + threshold && pdfY >= y1 - threshold && pdfY <= y2 + threshold) return i;
                } else if (shape.type === 'arrow') {
                    if (this.distToSegment(pdfX, pdfY, shape.x1, shape.y1, shape.x2, shape.y2) < threshold) return i;
                } else if (shape.type === 'pen' && shape.points) {
                    for (let j = 1; j < shape.points.length; j++) {
                        if (this.distToSegment(pdfX, pdfY, shape.points[j-1].x, shape.points[j-1].y, shape.points[j].x, shape.points[j].y) < threshold) return i;
                    }
                }
            }
            return -1;
        },

        distToSegment(px, py, x1, y1, x2, y2) {
            const dx = x2 - x1, dy = y2 - y1;
            const lenSq = dx * dx + dy * dy;
            if (lenSq === 0) return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
            let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
            t = Math.max(0, Math.min(1, t));
            return Math.sqrt((px - (x1 + t * dx)) ** 2 + (py - (y1 + t * dy)) ** 2);
        },

        drawArrow(ctx, fromX, fromY, toX, toY) {
            const headlen = 10;
            const dx = toX - fromX;
            const dy = toY - fromY;
            const angle = Math.atan2(dy, dx);
            ctx.beginPath();
            ctx.moveTo(fromX, fromY);
            ctx.lineTo(toX, toY);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(toX, toY);
            ctx.lineTo(toX - headlen * Math.cos(angle - Math.PI / 6), toY - headlen * Math.sin(angle - Math.PI / 6));
            ctx.moveTo(toX, toY);
            ctx.lineTo(toX - headlen * Math.cos(angle + Math.PI / 6), toY - headlen * Math.sin(angle + Math.PI / 6));
            ctx.stroke();
        },

        redrawAllShapes(key) {
            const canvas = key === 'top' ? this.$refs.topDrawCanvas : this.$refs.bottomDrawCanvas;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            // 画布实际分辨率放大了 dpr 倍，缩放上下文使绘制坐标保持 CSS 像素
            const dpr = window.devicePixelRatio || 1;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const s = this.getState(key);
            s.shapes.forEach((shape, idx) => {
                const color = shape.color || '#ff0000';
                const isSelected = idx === s.selectedShapeIndex;
                ctx.strokeStyle = color;
                ctx.lineWidth = isSelected ? 4 : 2;
                if (shape.type === 'circle') {
                    const pos = this.toCanvasCoords(shape.x, shape.y, s);
                    const r = shape.radius * (s.zoom / 100);
                    ctx.beginPath();
                    ctx.arc(pos.x, pos.y, r, 0, 2 * Math.PI);
                    ctx.stroke();
                    if (isSelected) {
                        ctx.strokeStyle = '#1890ff';
                        ctx.lineWidth = 1;
                        ctx.setLineDash([5, 5]);
                        ctx.stroke();
                        ctx.setLineDash([]);
                    }
                } else if (shape.type === 'rectangle') {
                    const p1 = this.toCanvasCoords(shape.x, shape.y, s);
                    const p2 = this.toCanvasCoords(shape.x + shape.w, shape.y + shape.h, s);
                    ctx.strokeRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
                    if (isSelected) {
                        ctx.strokeStyle = '#1890ff';
                        ctx.lineWidth = 1;
                        ctx.setLineDash([5, 5]);
                        ctx.strokeRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
                        ctx.setLineDash([]);
                    }
                } else if (shape.type === 'arrow') {
                    const p1 = this.toCanvasCoords(shape.x1, shape.y1, s);
                    const p2 = this.toCanvasCoords(shape.x2, shape.y2, s);
                    this.drawArrow(ctx, p1.x, p1.y, p2.x, p2.y);
                    if (isSelected) {
                        ctx.strokeStyle = '#1890ff';
                        ctx.lineWidth = 1;
                        ctx.setLineDash([5, 5]);
                        this.drawArrow(ctx, p1.x, p1.y, p2.x, p2.y);
                        ctx.setLineDash([]);
                    }
                } else if (shape.type === 'pen') {
                    ctx.beginPath();
                    const first = this.toCanvasCoords(shape.points[0].x, shape.points[0].y, s);
                    ctx.moveTo(first.x, first.y);
                    for (let i = 1; i < shape.points.length; i++) {
                        const pt = this.toCanvasCoords(shape.points[i].x, shape.points[i].y, s);
                        ctx.lineTo(pt.x, pt.y);
                    }
                    ctx.stroke();
                    if (isSelected) {
                        ctx.strokeStyle = '#1890ff';
                        ctx.lineWidth = 1;
                        ctx.setLineDash([5, 5]);
                        ctx.beginPath();
                        ctx.moveTo(first.x, first.y);
                        for (let i = 1; i < shape.points.length; i++) {
                            const pt = this.toCanvasCoords(shape.points[i].x, shape.points[i].y, s);
                            ctx.lineTo(pt.x, pt.y);
                        }
                        ctx.stroke();
                        ctx.setLineDash([]);
                    }
                }
            });
        },

        togglePopup(id, key) {
            const s = this.getState(key);
            const anno = s.annotations.find(a => a.id === id);
            if (!anno) return;
            s.activePopup = s.activePopup === id ? null : id;
            s.selectedAnnoId = id;
            this.$emit('select-anno', id, anno.replyContent || '');
        },

        startMarkerDrag(anno, e, key) {
            const s = this.getState(key);
            if (key === 'bottom') { this.togglePopup(anno.id, key); return; }
            if (!this.isNewAnno(anno)) { this.togglePopup(anno.id, key); return; }
            e.preventDefault();
            const pos = this.getPdfCoordinates(e, key);
            this.topState.selectedAnnoId = '';
            this.bottomState.selectedAnnoId = '';
            s.selectedShapeIndex = -1;
            s.selectedAnnoId = anno.id;
            const dragStartPdf = { x: anno.x, y: anno.y };
            const mouseStart = { x: pos.x, y: pos.y };
            let isClick = true;

            const onMove = (ev) => {
                const p = this.getPdfCoordinates(ev, key);
                const dx = p.x - mouseStart.x;
                const dy = p.y - mouseStart.y;
                if (Math.abs(dx) > 3 || Math.abs(dy) > 3) isClick = false;
                const vpw = s.pdfViewport ? s.pdfViewport.width : 1;
                const vph = s.pdfViewport ? s.pdfViewport.height : 1;
                anno.x = Math.max(0, Math.min(100, dragStartPdf.x + (dx / vpw) * 100));
                anno.y = Math.max(0, Math.min(100, dragStartPdf.y + (dy / vph) * 100));
            };

            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                if (isClick) this.togglePopup(anno.id, key);
            };

            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },

        toggleSplitDirection() {
            this.splitDirection = this.splitDirection === 'horizontal' ? 'vertical' : 'horizontal';
            this.$nextTick(() => {
                this.centerCanvas('top');
                this.centerCanvas('bottom');
            });
        },

        startDividerDrag(e) {
            e.preventDefault();
            const viewer = this.$el;
            const startX = e.clientX;
            const startY = e.clientY;
            const startPercent = this.dividerPercent;
            const isHorizontal = this.splitDirection === 'horizontal';

            const onMove = (ev) => {
                const rect = viewer.getBoundingClientRect();
                let delta;
                if (isHorizontal) {
                    delta = ev.clientX - startX;
                } else {
                    delta = ev.clientY - startY;
                }
                const dimension = isHorizontal ? rect.width : rect.height;
                const newPercent = Math.max(20, Math.min(80, startPercent + (delta / dimension) * 100));
                this.dividerPercent = newPercent;
                this.$nextTick(() => {
                    this.centerCanvas('top');
                    this.centerCanvas('bottom');
                });
            };

            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };

            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },

        getSaveData() {
            const now = new Date().toISOString();
            const pageComments = [];
            const collect = (key) => {
                const s = this.getState(key);
                const comments = [];
                s.annotations.forEach(anno => {
                    if (anno._historical) return;
                    const position = JSON.stringify({
                        type: "comment",
                        x: anno.x, y: anno.y,
                        popupOffsetX: anno.popupOffsetX,
                        popupOffsetY: anno.popupOffsetY
                    });
                    comments.push({
                        content: anno.content || "-",
                        createAt: now,
                        createBy: this.userId,
                        createName: this.userName,
                        position,
                        title: anno.title || "-",
                        angle: s.rotation
                    });
                });
                s.shapes.forEach(shape => {
                    if (shape._historical) return;
                    const sd = { ...shape };
                    delete sd._historical;
                    const position = JSON.stringify({
                        type: "shape", shapeType: shape.type, data: sd
                    });
                    comments.push({
                        content: "-", createAt: now,
                        createBy: this.userId, createName: this.userName,
                        position, title: "-",
                        angle: s.rotation
                    });
                });
                if (comments.length) {
                    pageComments.push({
                        pageId: key === 'top' ? this.topPageId : this.bottomPageId,
                        comments,
                    });
                }
            };
            collect('top');
            collect('bottom');
            return { pageComments };
        },

        loadHistoricalData(textAnnos, shapeList, rotation) {
            this.loadHistoricalDataForCanvas('top', textAnnos, shapeList, rotation);
            this.loadHistoricalDataForCanvas('bottom', textAnnos, shapeList, rotation);
        },

        loadHistoricalDataForCanvas(key, textAnnos, shapeList, rotation) {
            const s = this.getState(key);
            if (rotation !== undefined) s.rotation = rotation;
            s.annotations = (textAnnos || []).map(item => ({
                id: item.id,
                title: item.title === '-' ? '' : (item.title || ''),
                content: item.content === '-' ? '' : (item.content || ''),
                author: item.creator || '',
                createBy: item.createBy || '',
                createAt: item.createAt || '',
                createName: item.createName || '',
                x: item.x || 0, y: item.y || 0,
                popupOffsetX: item.popupOffsetX || '30px',
                popupOffsetY: item.popupOffsetY || '-10px',
                status: item.status || '',
                replyContent: item.replyContent || '',
                replyBy: item.replyBy || '',
                replyName: item.replyName || '',
                replyAt: item.replyAt || '',
                _historical: true,
            }));
            s.shapes = (shapeList || []).filter(s => s && s.type);
            if (s.pdfDoc) {
                this.renderPage(key);
            } else {
                this.redrawAllShapes(key);
            }
        },

        emitCacheSync(key) {
            const s = this.getState(key);
            const pageId = key === 'top' ? this.topPageId : this.bottomPageId;
            this.$emit('cache-sync', pageId, s.annotations, s.shapes, s.rotation);
        },

        clearAll() {
            this.topState.annotations = [];
            this.topState.shapes = [];
            this.bottomState.annotations = [];
            this.bottomState.shapes = [];
            this.redrawAllShapes('top');
            this.redrawAllShapes('bottom');
        },
        clearPdf(key) {
            const s = this.getState(key);
            if (s._pdfLoadingTask) {
                s._pdfLoadingTask.destroy();
                s._pdfLoadingTask = null;
            }
            if (s._renderTask) {
                s._renderTask.cancel();
                s._renderTask = null;
            }
            s.pdfDoc = null;
            s.pdfViewport = null;
            s.translateX = 0;
            s.translateY = 0;
            s.rotation = 0;
            this.$emit("update:" + key + "TotalPages", 0);
        },

        formatDateTime(isoStr) {
            if (!isoStr) return '';
            const d = new Date(isoStr);
            const pad = n => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        },

        removeAnnotation(id) {
            ['top', 'bottom'].forEach(key => {
                const s = this.getState(key);
                s.annotations = s.annotations.filter(a => a.id !== id);
                if (s.selectedAnnoId === id) s.selectedAnnoId = '';
                if (s.activePopup === id) s.activePopup = null;
            });
        },

        updateAnnotation(id, title, content) {
            ['top', 'bottom'].forEach(key => {
                const s = this.getState(key);
                const anno = s.annotations.find(a => a.id === id);
                if (anno) {
                    if (title !== undefined) anno.title = title;
                    if (content !== undefined) anno.content = content;
                }
            });
        },
        startReply(anno) {
            this.replyingAnnoId = anno.id;
            this.replyCache = anno.replyContent || '';
        },
        saveReply(anno) {
            if (!this.replyCache.trim()) return;
            anno.replyContent = this.replyCache;
            this.replyingAnnoId = '';
            this.replyCache = '';
            this.$emit('reply', { id: anno.id, content: anno.replyContent });
        },
        cancelReply() {
            this.replyingAnnoId = '';
            this.replyCache = '';
        },
        editReply(anno) {
            this.startReply(anno);
        },
        updateAnnotationReply(id, content) {
            ['top', 'bottom'].forEach(key => {
                const s = this.getState(key);
                const anno = s.annotations.find(a => a.id === id);
                if (anno) anno.replyContent = content;
            });
        },
        updateAnnotationStatus(id, status) {
            ['top', 'bottom'].forEach(key => {
                const s = this.getState(key);
                const anno = s.annotations.find(a => a.id === id);
                if (anno) anno.status = status;
            });
        },
    },
};
</script>

<style lang="less" scoped>
.contrast-viewer {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #f0f0f0;
    overflow: hidden;
}

.contrast-toolbar {
    position: absolute;
    top: 12px;
    right: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    padding: 8px 16px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    z-index: 99999;

    .sync-label {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 14px;
        color: #333;
        cursor: pointer;
        margin-right: 8px;
        user-select: none;
    }

    .separator {
        width: 1px;
        height: 24px;
        background: #e0e0e0;
        margin: 0 4px;
    }

    .color-label {
        display: flex;
        align-items: center;
        cursor: pointer;
        padding: 2px;
        border: 2px solid #ddd;
        border-radius: 4px;
        &:hover { border-color: #1890ff; }
    }

    .color-picker {
        width: 22px;
        height: 22px;
        border: none;
        padding: 0;
        cursor: pointer;
    }

    .tool-btn {
        border: none;
        background: transparent;
        font-size: 20px;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;
        &:hover { background: #f0f0f0; }
        &.active { background: #e6f7ff; }
        &:disabled { opacity: 0.3; cursor: not-allowed; }
    }

    .view-mode-tip {
        font-size: 14px;
        color: #999;
        padding: 4px 12px;
    }
}

.canvas-section {
    position: absolute;
    left: 0;
    right: 0;
    overflow: hidden;

    &.top-section {
        top: 0;
    }
    &.bottom-section {
        bottom: 0;
    }
}

.version-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    z-index: 15;

    &.top {
        background: #52c41a;
    }
    &.bottom {
        background: #ee8319;
    }
}

.readonly-badge {
    position: absolute;
    top: 8px;
    left: 100px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    color: #999;
    background: #f5f5f5;
    border: 1px solid #d9d9d9;
    z-index: 15;
    user-select: none;
}

.canvas-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #e9e9e9;
    cursor: default;

    &.hand-tool {
        cursor: grab;
        &:active { cursor: grabbing; }
    }

    .transform-layer {
        position: absolute;
        top: 0;
        left: 0;
        transform-origin: 0 0;
    }

    .base-image {
        position: absolute;
        border: none;
        top: 0;
        left: 0;
    }

    .draw-canvas {
        position: absolute;
        border: none;
        top: 0;
        left: 0;
        pointer-events: none;
    }
}

.annotation-marker {
    position: absolute;
    cursor: pointer;

    &.marker-new .marker-icon {
        cursor: move;
    }

    &.marker-selected > .marker-icon {
        box-shadow: 0 0 0 3px #1890ff;
    }

    &.marker-end .marker-icon {
        background: #52c41a;
    }

    .marker-icon {
        width: 32px;
        height: 32px;
        background: #ff4d4f;
        border-radius: 4px;
        position: relative;
        &::after {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 16px;
            height: 16px;
            background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>') center/cover;
        }
    }

    .popup-card {
        position: absolute;
        top: 0;
        left: 0;
        width: 260px;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        padding: 12px;
        display: none;
        z-index: 99999;
        &.active { display: block; }

        .popup-header {
            display: flex;
            align-items: center;
            margin-bottom: 8px;
            gap: 8px;
            .popup-title-input {
                flex: 1;
                border: none;
                font-weight: 600;
                font-size: 14px;
                outline: none;
                padding: 0;
            }
            .close-btn {
                border: none;
                background: transparent;
                font-size: 18px;
                cursor: pointer;
                color: #999;
                flex-shrink: 0;
            }
        }
        .popup-content {
            margin-bottom: 12px;
            .popup-content-textarea {
                width: 100%;
                border: 1px solid #eee;
                border-radius: 4px;
                padding: 8px;
                font-size: 13px;
                color: #666;
                line-height: 1.5;
                outline: none;
                resize: vertical;
                min-height: 60px;
                box-sizing: border-box;
            }
        }
        .reply-input-area {
            margin-bottom: 8px;

            .reply-textarea {
                width: 100%;
                border: 1px solid #d9d9d9;
                border-radius: 4px;
                padding: 6px 8px;
                font-size: 12px;
                resize: vertical;
                min-height: 50px;
                outline: none;
                box-sizing: border-box;
                &:focus { border-color: #1890ff; }
            }

            .reply-actions {
                display: flex;
                gap: 8px;
                margin-top: 6px;

                .save-reply-btn {
                    flex: 1;
                    border: none;
                    background: #1890ff;
                    color: #fff;
                    padding: 4px 0;
                    border-radius: 4px;
                    font-size: 12px;
                    cursor: pointer;
                    &:hover { background: #40a9ff; }
                }

                .cancel-reply-btn {
                    flex: 1;
                    border: 1px solid #d9d9d9;
                    background: #fff;
                    color: #666;
                    padding: 4px 0;
                    border-radius: 4px;
                    font-size: 12px;
                    cursor: pointer;
                    &:hover { border-color: #1890ff; color: #1890ff; }
                }
            }
        }
        .popup-reply {
            margin-bottom: 8px;
            padding: 6px 8px;
            background: #f6ffed;
            border: 1px solid #b7eb8f;
            border-radius: 4px;
            font-size: 12px;
            line-height: 1.5;
            .reply-label { color: #52c41a; font-weight: 500; }
            .reply-text { color: #333; }
        }
        .popup-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            .author { font-size: 12px; color: #999; }

            .reply-btn {
                font-size: 12px;
                color: #1890ff;
                cursor: pointer;
                &:hover { color: #40a9ff; }
            }

            .edit-reply-btn {
                font-size: 12px;
                color: #1890ff;
                cursor: pointer;
                &:hover { color: #40a9ff; }
            }
        }
    }
}

.canvas-controls {
    position: absolute;
    right: 12px;
    bottom: 12px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    z-index: 10;

    .zoom-controls {
        display: flex;
        align-items: center;
        background: #fff;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        .zoom-btn {
            border: none;
            background: #fff;
            padding: 6px 10px;
            font-size: 16px;
            cursor: pointer;
            &:hover { background: #f0f0f0; }
            &:disabled { opacity: 0.3; cursor: not-allowed; }
        }
        .zoom-level {
            text-align: center;
            padding: 4px 8px;
            font-size: 12px;
            color: #666;
            min-width: 45px;
        }
        .zoom-separator {
            height: 1px;
            background: #e0e0e0;
            margin: 4px 8px;
        }
        .rotate-icon {
            display: inline-block;
            transition: transform 0.2s ease;
        }
    }

    .pagination {
        display: flex;
        align-items: center;
        gap: 8px;
        background: rgba(0,0,0,0.7);
        color: #fff;
        padding: 4px 10px;
        border-radius: 6px;
        .page-btn {
            background: transparent;
            border: none;
            color: #fff;
            cursor: pointer;
            &:disabled { opacity: 0.4; cursor: not-allowed; }
        }
        .page-info { font-size: 12px; }
    }
}

.canvas-divider {
    position: absolute;
    left: 0;
    right: 0;
    height: 4px;
    background: #409eff;
    z-index: 20;
    cursor: row-resize;
    display: flex;
    align-items: center;
    justify-content: center;

    .divider-handle {
        width: 30px;
        height: 14px;
        background: #409eff;
        border-radius: 7px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 12px;
        line-height: 14px;
        margin-top: -5px;
        user-select: none;
    }
}

.contrast-viewer.horizontal {
    .canvas-section {
        &.top-section {
            top: 0;
            bottom: 0;
            right: auto;
            height: auto !important;
        }
        &.bottom-section {
            top: 0;
            bottom: 0;
            left: auto;
        }
    }
    .canvas-divider {
        top: 0;
        bottom: 0;
        left: 50%;
        height: auto;
        width: 4px;
        cursor: col-resize;
        flex-direction: column;
        .divider-handle {
            width: 14px;
            height: 30px;
            margin-top: 0;
            margin-left: -5px;
        }
    }
}
</style>
